/**
 * 期限換算（批108）——後端 `app/services/terms.py` 的前端鏡像。
 *
 * 憑證期限是**兩欄相加**：`總分鐘 = term_days * 1440 + term_minutes`。起因是要能核發
 * 臨時憑證（技術支援救援、內部測試／教育訓練），時間長度可調。
 *
 * ★★ 這份必須與後端逐條一致。分岔的症狀很難查：按鈕文案說「確認並核發」但送出去卻進了
 * 待審核（或反過來），因為兩邊對「這份內容等不等於套裝模板」算出不同答案。
 */

export const MINUTES_PER_DAY = 1440
export const MINUTES_PER_HOUR = 60
/** `term_minutes` 的合法上界（含）——超過就該進位成天，見 `normalizeTerm`。 */
export const MAX_TERM_MINUTES = MINUTES_PER_DAY - 1

/** 期限的輸入單位（表單下拉）。 */
export type TermUnit = 'day' | 'hour' | 'minute'

export const TERM_UNIT_LABELS: Record<TermUnit, string> = {
  day: '天',
  hour: '小時',
  minute: '分鐘',
}

function coerce(value: number | null | undefined): number {
  if (value == null || Number.isNaN(value)) return 0
  const n = Math.trunc(Number(value))
  return n > 0 ? n : 0
}

/** 總長度（分鐘）。**所有期限比較都用這個值**，不要比兩個欄位。 */
export function totalMinutes(days: number | null | undefined, minutes: number | null | undefined): number {
  return coerce(days) * MINUTES_PER_DAY + coerce(minutes)
}

/**
 * 有沒有「相對期限」。
 *
 * ★ 取代所有 `if (termDays)` 的寫法：分鐘級的證 `term_days` 是 **0**（falsy），
 *   truthy 判斷會把它看成「沒有期限」。
 */
export function hasTerm(days: number | null | undefined, minutes: number | null | undefined): boolean {
  return totalMinutes(days, minutes) > 0
}

/** 總分鐘 → `[term_days, term_minutes]` 的唯一表示法（餘數嚴格 0–1439）。 */
export function splitMinutes(total: number): [number, number] {
  const t = Math.max(0, Math.trunc(total))
  return [Math.floor(t / MINUTES_PER_DAY), t % MINUTES_PER_DAY]
}

/** 正規化成唯一表示法——**送出之前一定要過這支**，否則模板比對會誤判成偏離。 */
export function normalizeTerm(
  days: number | null | undefined,
  minutes: number | null | undefined,
): [number, number] {
  return splitMinutes(totalMinutes(days, minutes))
}

/** 給人看的期限：`3 天`／`2 小時`／`30 分鐘`／`1 天 1 小時 30 分鐘`／`無期限`。 */
export function formatTerm(
  days: number | null | undefined,
  minutes: number | null | undefined,
): string {
  const total = totalMinutes(days, minutes)
  if (total === 0) return '無期限'
  const [d, rest] = splitMinutes(total)
  const h = Math.floor(rest / MINUTES_PER_HOUR)
  const m = rest % MINUTES_PER_HOUR
  const parts: string[] = []
  if (d) parts.push(`${d} 天`)
  if (h) parts.push(`${h} 小時`)
  if (m) parts.push(`${m} 分鐘`)
  return parts.join(' ')
}

/**
 * 總分鐘 → 表單用的「數值＋單位」（挑**能整除的最大單位**）。
 *
 * `43200 → [30, 'day']`、`120 → [2, 'hour']`、`90 → [90, 'minute']`。
 * 90 分鐘刻意不變成「1.5 小時」——欄位是整數輸入，小數會在送出時被截掉。
 */
export function toUnitValue(total: number): [number, TermUnit] {
  const t = Math.max(0, Math.trunc(total))
  if (t === 0) return [0, 'day']
  if (t % MINUTES_PER_DAY === 0) return [t / MINUTES_PER_DAY, 'day']
  if (t % MINUTES_PER_HOUR === 0) return [t / MINUTES_PER_HOUR, 'hour']
  return [t, 'minute']
}

/** 「數值＋單位」→ 總分鐘。 */
export function fromUnitValue(value: number | null | undefined, unit: TermUnit): number {
  const n = coerce(value)
  if (unit === 'day') return n * MINUTES_PER_DAY
  if (unit === 'hour') return n * MINUTES_PER_HOUR
  return n
}

/**
 * 剩餘時間，自動選粒度（批108 裁示 26）。
 *
 * 期限的最小單位降到分鐘之後，一律印天數會讓所有短期憑證都顯示「剩 0 天」。
 * ★ 三個 realm 與 CS 站台的授權頁用同一套措辭（後端 `_humanize_remaining`）。
 */
export function formatRemaining(expiresAt: string | null | undefined, now: Date = new Date()): string | null {
  if (!expiresAt) return null
  const end = new Date(expiresAt).getTime()
  if (Number.isNaN(end)) return null
  const diffMinutes = Math.floor((end - now.getTime()) / 60000)
  if (diffMinutes < 0) {
    const over = Math.abs(diffMinutes)
    if (over >= MINUTES_PER_DAY) return `已過期 ${Math.floor(over / MINUTES_PER_DAY)} 天`
    if (over >= MINUTES_PER_HOUR) return `已過期 ${Math.floor(over / MINUTES_PER_HOUR)} 小時`
    return `已過期 ${over} 分鐘`
  }
  if (diffMinutes >= MINUTES_PER_DAY) return `剩 ${Math.floor(diffMinutes / MINUTES_PER_DAY)} 天`
  if (diffMinutes >= MINUTES_PER_HOUR) return `剩 ${Math.floor(diffMinutes / MINUTES_PER_HOUR)} 小時`
  return `剩 ${diffMinutes} 分鐘`
}

/** 總長度不足一天＝短期證（UI 標「短期」膠囊、到期告警排除都用這條線）。 */
export function isShortTerm(
  days: number | null | undefined,
  minutes: number | null | undefined,
): boolean {
  const total = totalMinutes(days, minutes)
  return total > 0 && total < MINUTES_PER_DAY
}
